import { ResourceSearchCardProps } from "@/common/card-types.ts";
import { isEmpty } from "@utils/var.check.ts";
import SecurityUtils from "@utils/security.utils.ts";
import StringUtils from "@utils/string.utils.ts";
import WhitelistWebsiteDetailsContentSubpage from "@modules/whitelist-website/components/view/WhitelistWebsiteDetailsContentSubpage.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { WebsiteResultItem } from "@modules/resource-search/resource-search-types.ts";
import { useI18n } from "vue-i18n";

export const useWebsiteSearchCard = () => {
  const { t } = useI18n();
  const contentSubpageInstance = useContentSubpageInstance();

  const populateWebsiteSearchCardProps = (
    resourceData: WebsiteResultItem,
    showOpenInNewTab: boolean,
  ): ResourceSearchCardProps => {
    let title = resourceData?.name ?? "";
    if (!isEmpty(resourceData?.highlights?.name)) {
      title = resourceData.highlights.name ?? "";
    }
    return {
      title: SecurityUtils.sanitizeHTMLForHighlight(title) ?? "",
      type: "WEBSITE",
      content: generateContent(resourceData),
      iconUrl: resourceData.iconUrl,
      ariaLabelConfig: {
        cardComponent: t("search_view_details_aria_label", {
          title: SecurityUtils.stripHtml(title),
        }),
        openInNewTab: t("search_result_module_card_open_in_new_tab_aria_label"),
      },
      showOpenInNewTab,
    };
  };

  const generateContent = (resourceData: WebsiteResultItem): string => {
    if (!isEmpty(resourceData.highlights.description)) {
      return (
        "[ <span>Description</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(resourceData.highlights.description)
      );
    }

    if (!isEmpty(resourceData.highlights.keyword)) {
      return (
        "[ <span>Tags</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(
          StringUtils.formatSpaceAfterComma(resourceData.highlights.keyword),
        )
      );
    }

    return SecurityUtils.sanitizeHTMLForHighlight(
      resourceData.description ? resourceData.description : "",
    );
  };
  const populateWebsiteSearchCardListeners = (
    resourceData: WebsiteResultItem,
  ): { click: () => void; openInNewTab: () => void } => {
    return {
      click: () => handleClick(resourceData),
      openInNewTab: () => handleOpenInNewTab(resourceData),
    };
  };

  const handleClick = (resourceData: WebsiteResultItem): void => {
    contentSubpageInstance.show(WhitelistWebsiteDetailsContentSubpage, {
      highlights: resourceData.highlights,
      baseTitle: [resourceData.name],
      websiteUuid: resourceData.uuid,
    });
  };

  const handleOpenInNewTab = (resourceData: WebsiteResultItem): void => {
    if (!isEmpty(resourceData.homeUrl)) {
      window.open(resourceData.homeUrl, "_blank", "noreferrer,noopener");
    }
  };

  return {
    populateWebsiteSearchCardProps,
    populateWebsiteSearchCardListeners,
    handleOpenInNewTab,
  };
};
